import type {
  DataDumpAggregateType,
  PnlCategoryObjOfTagTimes,
  PnlLoss,
  RollupWithTagsAndCountsAndRange,
  TagsWithMeasurementObj,
  TimeRangeFinite,
} from "../../../../types/types";
import { EMPTY_TREND_BAR } from "../trendChartUtils";
import { cloneDeep } from "lodash";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "constants/customers";
import { CHANGEOVER_DOWNTIME } from "constants/constants";
import { getTotalProducingTimeForProducingLosses } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategoriesWithProducingLosses";
import { objectFromEntries } from "@raven/shared/utils";
import { useTrendChartBarsArray } from "utils/customHooks/trendChartHooks";
import {
  useGetPageFromPath,
  useDoesCustomerShowProducing,
} from "utils/customHooks/genericHooks";
import { useCallback } from "react";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { TopDownOrBottomUp } from "components/DataGetter/FlightConfig/FlightConfigTypes/GeneralConfigType";

/** convert a Midgard DataDumpAggregateType to RollupWithTagsAndCountsAndRange, legacy type used in various chart components */
export function useMapDataDumpToRollup(): (
  dataDump: DataDumpAggregateType
) => RollupWithTagsAndCountsAndRange {
  const ckey = useCustomerKey();
  const doesCustomerShowProducing = useDoesCustomerShowProducing();
  const [
    {
      general: {
        includeDependentLosses,
        hideProducingLosses,
        topDownOrBottomUp,
        convertQualityLossToProducing,
        producingTimeFromDataDumpAggregate,
      },
    },
  ] = useFlightConfig();

  return useCallback(
    (dataDump: DataDumpAggregateType) =>
      mapDataDumpToRollup({
        dataDump,
        ckey,
        doesCustomerShowProducing,
        topDownOrBottomUp,
        hideProducingLosses,
        convertQualityLossToProducing,
        includeDependentLosses,
        producingTimeFromDataDumpAggregate,
      }),
    [
      ckey,
      doesCustomerShowProducing,
      topDownOrBottomUp,
      hideProducingLosses,
      includeDependentLosses,
      producingTimeFromDataDumpAggregate,
      convertQualityLossToProducing,
    ]
  );
}

// tslint:disable-next-line: cognitive-complexity
/** convert a Midgard DataDumpAggregateType to RollupWithTagsAndCountsAndRange, legacy type used in various chart components */
function mapDataDumpToRollup({
  dataDump,
  ckey,
  doesCustomerShowProducing,
  topDownOrBottomUp,
  hideProducingLosses,
  convertQualityLossToProducing,
  includeDependentLosses,
  producingTimeFromDataDumpAggregate,
}: {
  dataDump: DataDumpAggregateType;
  ckey: string;
  doesCustomerShowProducing: boolean;
  topDownOrBottomUp: TopDownOrBottomUp;
  hideProducingLosses: boolean;
  convertQualityLossToProducing: boolean;
  includeDependentLosses: boolean;
  producingTimeFromDataDumpAggregate: boolean;
}): RollupWithTagsAndCountsAndRange {
  const downtimeLossMs_or_downtimePlusDependentSpeedLossMs: keyof PnlLoss =
    hideProducingLosses || !includeDependentLosses
      ? "downtimeLossMs"
      : "downtimePlusDependentSpeedLossMs";
  const {
    // processNames,
    // producingTime,
    // speedLossTime,
    // minorStopTime,
    // slowdownLossTime,
    // rejectLossTime,
    // plannedTime,
    // waitingTime,
    // unplannedTime,
    // unknownTime,
    notScheduledTime,
    uncollectedTime,
    // effectiveProducingTimeBottomUp,
    netOutput,
    grossOutput,
    // rejectOutput,
    pnlLosses,
    meanTimeBetweenFailures,
    meanTimeToRecovery,
    avgCycleTime,
  } = {
    notScheduledTime: dataDump.notScheduledTime || 0,
    uncollectedTime: dataDump.uncollectedTime || 0,
    netOutput: dataDump.netOutput || 0,
    grossOutput: dataDump.grossOutput || 0,
    pnlLosses: dataDump.pnlLosses || 0,
    meanTimeBetweenFailures: dataDump.meanTimeBetweenFailures || 0,
    meanTimeToRecovery: dataDump.meanTimeToRecovery || 0,
    avgCycleTime: dataDump.avgCycleTime || 0,
  };

  const producingTime = !hideProducingLosses
    ? getTotalProducingTimeForProducingLosses({
        dataDumpWithProducingLosses: dataDump,
        doesCustomerShowProducing,
        topDownOrBottomUp,
        producingTimeFromDataDumpAggregate,
      }).totalProducingTime
    : dataDump.producingTime || 0;

  // If this is a customer that uses retroactive tagging and has the changeover checkbox,
  // we need to change anything reallocated as changeover to light blue in the trend chart
  const usesAllocationAndChangeovers =
    CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey]?.usesChangeovers;
  const modifiedPnlLosses = usesAllocationAndChangeovers
    ? cloneDeep(pnlLosses)
    : pnlLosses;

  // set blank object for mapping any tags with C/O marked as true
  const changeoverCategory = {
    category: CHANGEOVER_DOWNTIME,
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    tags: [] as any[],
  };

  if (usesAllocationAndChangeovers) {
    const categoriesToApplyChangeoverTo =
      CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey]
        .categoriesToApplyChangeoversTo || [];

    // Loop through the categories that we want to check for changeover allocation
    categoriesToApplyChangeoverTo.forEach(currentCategory => {
      const categoryPnlLosses = modifiedPnlLosses?.[currentCategory];
      if (categoryPnlLosses?.tags) {
        modifiedPnlLosses[currentCategory].tags = modifiedPnlLosses[
          currentCategory
        ].tags.reduce((acc, tag) => {
          // If tag has the property changeoverTime, that means it was allocated as C/O
          // We need to add the changeover tag duration to the "changeoverDowntime" category
          // and remove it from the original category it was in
          if (tag?.changeoverTime) {
            // push into the changeoverTags array
            changeoverCategory.tags.push(tag);

            // add tag times to category times
            changeoverCategory[
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs
            ] += tag.changeoverTime;
            changeoverCategory.downtimeLossMs += tag.changeoverTime;
            changeoverCategory.downtimeDependentSpeedLossMs +=
              tag.changeoverTime;

            // and subtract from the original pnlLoss category
            categoryPnlLosses[
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs
            ] -= tag.changeoverTime;
            categoryPnlLosses.downtimeLossMs -= tag.changeoverTime;
            categoryPnlLosses.downtimeDependentSpeedLossMs -=
              tag.changeoverTime;
          } else {
            // leave it in this array
            acc = [...acc, tag];
          }
          return acc;
        }, [] as any[]);
      }
    });
  }

  const qualityLossTime = modifiedPnlLosses?.quality.qualityLossMs;
  const unknownDowntime = modifiedPnlLosses?.unknown.allocatableTimeMs || 0;

  const totalDurationMs =
    notScheduledTime +
    producingTime +
    uncollectedTime +
    (!modifiedPnlLosses
      ? 0
      : modifiedPnlLosses.planned[
          downtimeLossMs_or_downtimePlusDependentSpeedLossMs
        ] +
        modifiedPnlLosses.unplanned[
          downtimeLossMs_or_downtimePlusDependentSpeedLossMs
        ] +
        modifiedPnlLosses.waiting[
          downtimeLossMs_or_downtimePlusDependentSpeedLossMs
        ] +
        changeoverCategory[downtimeLossMs_or_downtimePlusDependentSpeedLossMs] +
        qualityLossTime +
        unknownDowntime);

  const tags: PnlCategoryObjOfTagTimes = {
    notScheduled: reduceTagsToObject(dataDump.pnlLosses.notScheduled.tags),
    planned: reduceTagsToObject(dataDump.pnlLosses.planned.tags),
    producing: {},
    uncollected: {},
    unknown: {},
    unplanned: reduceTagsToObject(dataDump.pnlLosses.unplanned.tags),
    waiting: reduceTagsToObject(dataDump.pnlLosses.waiting.tags),
  };

  return {
    ...EMPTY_TREND_BAR,
    range: dataDump.range,
    // in the trend chart bar we show both quality and speed loss as light green
    qualityLoss:
      hideProducingLosses || convertQualityLossToProducing
        ? 0
        : qualityLossTime,
    notScheduled: notScheduledTime,
    planned:
      modifiedPnlLosses?.planned?.[
        downtimeLossMs_or_downtimePlusDependentSpeedLossMs
      ],
    producing:
      producingTime + (convertQualityLossToProducing ? qualityLossTime : 0),
    totalDurationMs,
    uncollected: uncollectedTime,
    unknown:
      hideProducingLosses && !convertQualityLossToProducing
        ? qualityLossTime + unknownDowntime
        : unknownDowntime,
    unplanned:
      modifiedPnlLosses?.unplanned?.[
        downtimeLossMs_or_downtimePlusDependentSpeedLossMs
      ],
    waiting:
      modifiedPnlLosses?.waiting?.[
        downtimeLossMs_or_downtimePlusDependentSpeedLossMs
      ],
    // show special changeoverDowntime category
    [CHANGEOVER_DOWNTIME]: changeoverCategory.downtimeDependentSpeedLossMs,
    netOutput,
    grossOutput,
    tags,
    meanTimeBetweenFailures,
    meanTimeToRecovery,
    avgCycleTime,
    plannedDowntimeEfficiency: dataDump.plannedDowntimeEfficiency || undefined,
  };
}

export const EMPTY_BIGGEST_DURATION_ROLLUP = {
  rollupMetric: {
    counts: {
      producing: {},
      planned: {},
      unplanned: {},
      waiting: {},
      unknown: {},
      notScheduled: {},
      uncollected: {},
    },
    notScheduled: 0,
    planned: 0,
    producing: 0,
    totalDurationMs: 3600000,
    uncollected: 0,
    unknown: 0,
    unplanned: 0,
    waiting: 0,
    tags: {
      notScheduled: {},
      planned: {},
      producing: {},
      uncollected: {},
      unknown: {},
      unplanned: {},
      waiting: {},
    },
    range: {
      ge: 0,
      lt: 1,
    },
    qualityLoss: 0,
    netOutput: 0,
    grossOutput: 0,
    cumulativeOutput: 0,
  },
  duration: 0,
};

export const BAR_PNL_LOSS_ORDER = {
  notScheduled: 1,
  [CHANGEOVER_DOWNTIME]: 2,
  planned: 3,
  unknown: 4,
  qualityLoss: 5,
  unplanned: 6,
  waiting: 7,
};

export function sliceRangeAtBoundaries({
  range,
  min,
  max,
}: {
  range: TimeRangeFinite;
  min: number;
  max: number;
}) {
  return {
    ge: Math.max(range.ge, min),
    lt: Math.min(range.lt, max),
  };
}

function reduceTagsToObject(tags: any[]) {
  return objectFromEntries(
    tags.map(tag => [tag.name, tag.downtimePlusDependentSpeedLossMs])
  ) as TagsWithMeasurementObj;
}

/** the current cumulative output - can be used to prevent the trend line from overflowing past the top of the chart */
export function useCurrentTrendChartBarOutput() {
  const { isTrendsPage } = useGetPageFromPath();
  const rollupMetricTrendChartBarsArray = useTrendChartBarsArray(isTrendsPage);

  const now = Date.now();
  const currentBar = rollupMetricTrendChartBarsArray.find(
    ({ range: { ge, lt } }) => ge <= now && now < lt
  );
  return currentBar?.cumulativeOutput;
}
